/**
 * EndOfDayNudge - Contextual nudge banners for end-of-day clock out.
 *
 * Two mutually exclusive banners (priority: allStopsDone > postLastLog):
 * 1. "All Stops Done" — shown when all stops are complete and still clocked in
 * 2. "30 min since last stop" — shown when 30+ minutes have elapsed since last log
 *
 * Returns null if nothing to show.
 *
 * @example
 * <EndOfDayNudge
 *   allStopsDone={true}
 *   minutesSinceLastLog={45}
 *   isClockedIn={true}
 *   onClockOut={() => refetch()}
 * />
 */

import { useState, useCallback } from 'react';
import { useClockOut } from '../../../hooks/useTimesheets';
import { captureGpsOrNull } from '../utils/captureGpsOrNull';

interface EndOfDayNudgeProps {
  /** Whether all scheduled stops are marked complete */
  allStopsDone: boolean;
  /** Minutes since the last service log entry, or null if none */
  minutesSinceLastLog: number | null;
  /** Whether the employee is currently clocked in */
  isClockedIn: boolean;
  /** Optional callback fired after successful clock-out */
  onClockOut?: () => void;
}

export function EndOfDayNudge({
  allStopsDone,
  minutesSinceLastLog,
  isClockedIn,
  onClockOut,
}: EndOfDayNudgeProps) {
  const clockOut = useClockOut();
  const [capturingGps, setCapturingGps] = useState(false);
  const [allStopsDismissed, setAllStopsDismissed] = useState(() => {
    try { return sessionStorage.getItem('twg:time:allStopsDismissed') === 'true'; } catch { return false; }
  });
  const [snoozed, setSnoozed] = useState(false);

  const handleClockOut = useCallback(async () => {
    setCapturingGps(true);
    const gps = await captureGpsOrNull();
    setCapturingGps(false);
    clockOut.mutate(gps ?? undefined, {
      onSuccess: () => {
        onClockOut?.();
      },
    });
  }, [clockOut, onClockOut]);

  const isLoading = capturingGps || clockOut.isPending;

  // Must be clocked in for any nudge
  if (!isClockedIn) return null;

  // Banner 1: All stops done (higher priority)
  if (allStopsDone && !allStopsDismissed) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 animate-fade-in">
        <p className="text-green-800 font-medium text-sm mb-2">
          All stops complete! Great work today.
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={handleClockOut}
            disabled={isLoading}
            className="px-4 py-2 rounded-lg bg-green-700 hover:bg-green-800 text-white text-sm font-semibold active:scale-95 transition-all disabled:opacity-60"
          >
            {isLoading ? 'Capturing location...' : 'Clock Out'}
          </button>
          <button
            onClick={() => { setAllStopsDismissed(true); try { sessionStorage.setItem('twg:time:allStopsDismissed', 'true'); } catch { /* sessionStorage unavailable */ } }}
            className="text-green-600 text-sm hover:text-green-800 transition-colors"
          >
            Later
          </button>
        </div>
        {clockOut.isError && (
          <p className="text-red-600 text-sm mt-2">
            {(clockOut.error as Error).message}
          </p>
        )}
      </div>
    );
  }

  // Banner 2: 30 minutes since last stop
  if (minutesSinceLastLog !== null && minutesSinceLastLog >= 30 && !snoozed) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 animate-fade-in">
        <p className="text-amber-800 font-medium text-sm mb-2">
          30 minutes since your last stop.
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={handleClockOut}
            disabled={isLoading}
            className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold active:scale-95 transition-all disabled:opacity-60"
          >
            {isLoading ? 'Capturing location...' : 'Clock Out'}
          </button>
          <button
            onClick={() => setSnoozed(true)}
            className="text-amber-600 text-sm hover:text-amber-800 transition-colors"
          >
            Still Working
          </button>
        </div>
        {clockOut.isError && (
          <p className="text-red-600 text-sm mt-2">
            {(clockOut.error as Error).message}
          </p>
        )}
      </div>
    );
  }

  return null;
}
